import { useEffect, useState } from 'react';
import * as civilAdviseService from '../../../services/civilAdviseCaseRequests';
import type { LookupItem } from '../../../mocks/criminalMasterData';

export type CivilAdviseLookupScope = 'step1' | 'step2' | 'step3' | 'step4' | 'review';

type LookupBundle = {
  provinces: LookupItem[];
  districts: LookupItem[];
  urgencyLevels: LookupItem[];
  deliveryMethods: LookupItem[];
  corporateInstitutes: LookupItem[];
};

const EMPTY: LookupBundle = {
  provinces: [],
  districts: [],
  urgencyLevels: [],
  deliveryMethods: [],
  corporateInstitutes: [],
};

let sharedLookups: LookupBundle = { ...EMPTY };

export function useCivilAdviseLookups(scope: CivilAdviseLookupScope, provinceId?: string) {
  const [data, setData] = useState<LookupBundle>(sharedLookups);
  const [loading, setLoading] = useState(scope !== 'step4');

  useEffect(() => {
    if (scope === 'step4') {
      setLoading(false);
      return;
    }

    let mounted = true;
    setLoading(true);

    const load = async (): Promise<Partial<LookupBundle>> => {
      if (scope === 'step1') {
        const [prov, corp] = await Promise.all([
          civilAdviseService.getProvinces(),
          civilAdviseService.getCorporateInstitutes(),
        ]);
        return {
          provinces: prov.success ? prov.data : [],
          corporateInstitutes: corp.success ? corp.data : [],
        };
      }
      if (scope === 'step2') {
        const urg = await civilAdviseService.getUrgencyLevels();
        return { urgencyLevels: urg.success ? urg.data : [] };
      }
      if (scope === 'step3') {
        const del = await civilAdviseService.getDeliveryMethods();
        return { deliveryMethods: del.success ? del.data : [] };
      }
      const [prov, urg, del, corp] = await Promise.all([
        civilAdviseService.getProvinces(),
        civilAdviseService.getUrgencyLevels(),
        civilAdviseService.getDeliveryMethods(),
        civilAdviseService.getCorporateInstitutes(),
      ]);
      return {
        provinces: prov.success ? prov.data : [],
        urgencyLevels: urg.success ? urg.data : [],
        deliveryMethods: del.success ? del.data : [],
        corporateInstitutes: corp.success ? corp.data : [],
      };
    };

    void load().then((partial) => {
      if (!mounted) return;
      sharedLookups = { ...sharedLookups, ...partial };
      setData({ ...sharedLookups });
      setLoading(false);
    });

    return () => {
      mounted = false;
    };
  }, [scope]);

  useEffect(() => {
    if (scope !== 'step1' && scope !== 'review') return;
    if (!provinceId) {
      setData((d) => ({ ...d, districts: [] }));
      return;
    }

    let mounted = true;
    void civilAdviseService.getDistricts(provinceId).then((r) => {
      if (!mounted) return;
      const districts = r.success ? r.data : [];
      sharedLookups = { ...sharedLookups, districts };
      setData((d) => ({ ...d, districts }));
    });
    return () => {
      mounted = false;
    };
  }, [scope, provinceId]);

  return {
    loading,
    provinces: data.provinces,
    districts: data.districts,
    urgencyLevels: data.urgencyLevels,
    deliveryMethods: data.deliveryMethods,
    corporateInstitutes: data.corporateInstitutes,
  };
}
